import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  WaveCloudAPI,
  WaveGraphQLError,
  WaveTemplateCatalog,
  WaveStatus,
  activeWaves,
  buildWaveInboxSections,
  encodeSnapshotB64,
  encodeFullYDocSnapshotB64,
  isWaveYjsEnabled,
  seedYDocFromBlocks,
  stampTemplateBlocksForCreation,
  WAVE_FORMAT_YJS_V1,
  isReservedWaveSpace,
  isSharedWaveSpace,
  isOpenSeaWaveSpaceId,
  waveSpaceDisplayTitle,
  type WaveCoverKeys,
  type WaveOceanSpaceRecord,
  type WaveRecord,
  type WaveTemplate,
} from "@vida/wave-core";
import { WaveSpaceMembersPanel } from "../components/shell/WaveSpaceMembersPanel";
import { WaveThreadGallery } from "../components/library/WaveThreadGallery";
import { WaveBoardView } from "../components/board/WaveBoardView";
import { WaveBrandHeader, WaveSidebarNavIcon } from "../components/shell/WaveBrandHeader";
import { WaveSearchField } from "../components/shell/WaveSearchField";
import { WaveSidebarBottomCards } from "../components/shell/WaveSidebarBottomCards";
import { WaveShellToggleButton } from "../components/shell/WaveShellToggleButton";
import { WaveWorkspace } from "../components/shell/WaveWorkspace";
import { WaveAccountPage } from "./WaveAccountPage";
import { WaveActivityPage } from "./WaveActivityPage";
import { useWaveList, useWaveSpaces, useParticipatingWaves, useSharedByMeWaves, useOwnedWaves } from "../hooks/useWave";
import { createWaveApiConfig, ensureWaveCloudProfile } from "../services/waveSession";
import { appendWaveSyncLog } from "../services/waveSyncLog";
import { mergeWaveStatus } from "../utils/waveRecord";
import { needsAnyWaveCover, requestWaveCover } from "../services/waveCoverRequest";
import { useWaveFavorites } from "../services/waveFavorites";
import { useWaveLastOpened } from "../services/waveLastOpened";
import {
  filterInboxSectionsBySearch,
  filterSpacesBySearch,
  filterWavesBySearch,
} from "../utils/waveSearchFilter";
import { startLogin, loadTokens, currentUserEmail, currentUserSub } from "../auth";

const SIDEBAR_COLLAPSED_KEY = "wave.sidebar.collapsed";

/** GraphQL gate reasons that trigger auto-join when opening a shared link (`/w/:waveId`). */
const JOINABLE_REASONS = new Set(["not_participant", "not_member_not_participant"]);

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function waveListPathLabel(path: string, spaces: WaveOceanSpaceRecord[]): string {
  if (path === "/inbox") return "Inbox";
  if (path === "/favorites") return "Favoriti";
  if (path === "/shared") return "Condivisi";
  if (path === "/board") return "Board";
  if (path.startsWith("/s/")) {
    const spaceId = path.slice(3);
    const space = spaces.find((s) => s.spaceId === spaceId);
    return space ? waveSpaceDisplayTitle(space) : "Spazio";
  }
  return "Indietro";
}

function spaceTitleById(
  spaces: WaveOceanSpaceRecord[],
  spaceId: string | null,
  fallback = "Spazio",
): string {
  if (!spaceId) return fallback;
  const space = spaces.find((s) => s.spaceId === spaceId);
  return space ? waveSpaceDisplayTitle(space) : fallback;
}

export function ShellPage() {
  const { waveId: routeWaveId, spaceId: routeSpaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(loadTokens());

  const { spaces, setSpaces, refreshSpaces } = useWaveSpaces(isLoggedIn);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const isOpenSeasView = isOpenSeaWaveSpaceId(selectedSpaceId);
  const { waves, setWaves, refresh: refreshWaves } = useWaveList(
    isOpenSeasView ? null : selectedSpaceId,
    isLoggedIn,
  );
  const { sharedWaves, refresh: refreshParticipating } = useParticipatingWaves(isLoggedIn);
  const { sharedByMeWaves, refresh: refreshSharedByMe } = useSharedByMeWaves(isLoggedIn);
  const { ownedWaves, refresh: refreshOwnedWaves } = useOwnedWaves(isLoggedIn);
  const [activeWave, setActiveWave] = useState<WaveRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [presenceError, setPresenceError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [spaceDialog, setSpaceDialog] = useState<{
    spaceId: string;
    title: string;
    mode: "rename" | "delete";
    waveCount: number;
  } | null>(null);
  const [spaceDialogTitle, setSpaceDialogTitle] = useState("");
  const [waveRenameDialog, setWaveRenameDialog] = useState<{
    waveId: string;
    title: string;
  } | null>(null);
  const [waveRenameTitle, setWaveRenameTitle] = useState("");
  const [membersSpace, setMembersSpace] = useState<WaveOceanSpaceRecord | null>(null);
  const [searchText, setSearchText] = useState("");
  const { waveIds: favoriteWaveIds, toggle: toggleFavorite, contains: isFavoriteWave } =
    useWaveFavorites();
  const { recordOpen, lastOpenedEpochMs } = useWaveLastOpened();

  const spaceInitRef = useRef(false);
  const loadRequestRef = useRef(0);
  const [returnListPath, setReturnListPath] = useState("/inbox");

  const targetWaveId = routeWaveId ?? null;

  const browseScope = useMemo((): "none" | "inbox" | "favorites" | "shared" | "space" | "board" => {
    if (location.pathname === "/inbox") return "inbox";
    if (location.pathname === "/favorites") return "favorites";
    if (location.pathname === "/shared") return "shared";
    if (location.pathname === "/board") return "board";
    if (routeSpaceId && !targetWaveId) return "space";
    return "none";
  }, [location.pathname, routeSpaceId, targetWaveId]);

  const captureReturnListPath = useCallback(() => {
    const path = location.pathname;
    if (
      path === "/inbox" ||
      path === "/favorites" ||
      path === "/shared" ||
      path === "/board"
    ) {
      setReturnListPath(path);
    } else if (routeSpaceId && !targetWaveId) {
      setReturnListPath(`/s/${routeSpaceId}`);
    }
  }, [location.pathname, routeSpaceId, targetWaveId]);

  const navigateToWave = useCallback(
    (wave: WaveRecord) => {
      captureReturnListPath();
      navigate(`/w/${wave.waveId}`);
    },
    [captureReturnListPath, navigate],
  );

  const closeWaveDetail = useCallback(() => {
    navigate(returnListPath);
  }, [navigate, returnListPath]);

  const backListLabel = useMemo(
    () => waveListPathLabel(returnListPath, spaces),
    [returnListPath, spaces],
  );

  useEffect(() => {
    setSidebarOpen(false);
  }, [targetWaveId, routeSpaceId]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!targetWaveId) {
      setActiveWave((prev) => (prev === null ? prev : null));
      return;
    }
    const requestId = ++loadRequestRef.current;
    void (async () => {
      const cfg = createWaveApiConfig();
      try {
        let full = await WaveCloudAPI.getWave(cfg, targetWaveId);
        if (loadRequestRef.current !== requestId) return;
        setActiveWave(full);
        setError(null);
        recordOpen(targetWaveId);
        if (full.participants.length === 0) {
          appendWaveSyncLog(
            "presence",
            "getWave: nessun partecipante nel roster",
            full.waveId,
          );
        }
        if (needsAnyWaveCover(full)) {
          void requestWaveCover(full, false, (keys) => {
            patchWaveCover(full.waveId, keys);
          });
        }
      } catch (e) {
        if (loadRequestRef.current !== requestId) return;
        if (e instanceof WaveGraphQLError && e.reason && JOINABLE_REASONS.has(e.reason)) {
          try {
            await WaveCloudAPI.joinWaveViaLink(cfg, targetWaveId);
            await ensureWaveCloudProfile();
            const full = await WaveCloudAPI.getWave(cfg, targetWaveId);
            if (loadRequestRef.current !== requestId) return;
            setActiveWave(full);
            setError(null);
            recordOpen(targetWaveId);
            if (needsAnyWaveCover(full)) {
              void requestWaveCover(full, false, (keys) => {
                patchWaveCover(full.waveId, keys);
              });
            }
            return;
          } catch (joinErr) {
            setError(
              joinErr instanceof Error
                ? joinErr.message
                : "Impossibile unirsi alla wave",
            );
            return;
          }
        }
        setError(e instanceof Error ? e.message : "Errore apertura wave");
      }
    })();
  }, [targetWaveId, recordOpen]);

  useEffect(() => {
    if (browseScope !== "inbox" || !isLoggedIn) return;
    void refreshOwnedWaves();
    void refreshSharedByMe();
  }, [browseScope, isLoggedIn, refreshOwnedWaves, refreshSharedByMe]);

  useEffect(() => {
    if (routeSpaceId) {
      setSelectedSpaceId(routeSpaceId);
    }
  }, [routeSpaceId]);

  useEffect(() => {
    if (spaceInitRef.current || !spaces.length || selectedSpaceId) return;
    spaceInitRef.current = true;
    if (routeSpaceId) {
      setSelectedSpaceId(routeSpaceId);
    } else {
      // Parità Tide: non auto-selezionare Open Seas (riservato) se esistono spazi normali.
      const firstRegular = spaces.find((s) => !isReservedWaveSpace(s));
      const initial = firstRegular ?? spaces[0];
      if (initial) setSelectedSpaceId(initial.spaceId);
    }
  }, [spaces, selectedSpaceId, routeSpaceId]);

  const closeSidebar = () => setSidebarOpen(false);

  const openSpaceDialog = async (
    space: WaveOceanSpaceRecord,
    mode: "rename" | "delete",
  ) => {
    // Guard client-side: gli spazi riservati (Open Seas) non sono rinominabili/eliminabili.
    if (isReservedWaveSpace(space)) return;
    const cfg = createWaveApiConfig();
    let waveCount = 0;
    if (selectedSpaceId === space.spaceId && !isOpenSeasView) {
      waveCount = waves.length;
    } else {
      waveCount = (await WaveCloudAPI.listWaves(cfg, space.spaceId)).length;
    }
    setSpaceDialogTitle(space.title);
    setSpaceDialog({ spaceId: space.spaceId, title: space.title, mode, waveCount });
  };

  const submitSpaceDialog = async () => {
    if (!spaceDialog) return;
    const cfg = createWaveApiConfig();
    try {
      if (spaceDialog.mode === "rename") {
        const title = spaceDialogTitle.trim();
        if (!title) return;
        const updated = await WaveCloudAPI.updateOceanSpace(cfg, spaceDialog.spaceId, {
          title,
        });
        setSpaces((prev) =>
          prev.map((s) => (s.spaceId === updated.spaceId ? updated : s)),
        );
      } else {
        await WaveCloudAPI.deleteOceanSpace(cfg, spaceDialog.spaceId);
        setSpaces((prev) => prev.filter((s) => s.spaceId !== spaceDialog.spaceId));
        if (selectedSpaceId === spaceDialog.spaceId) {
          setSelectedSpaceId(null);
          setWaves([]);
          setActiveWave(null);
          navigate("/");
        }
      }
      setSpaceDialog(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Operazione spazio non riuscita");
    }
  };

  const patchWaveInList = useCallback((incoming: WaveRecord) => {
    setWaves((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (item.waveId !== incoming.waveId) return item;
        if (
          item.status === incoming.status &&
          item.title === incoming.title &&
          item.updatedAt === incoming.updatedAt &&
          item.coverImageS3Key === incoming.coverImageS3Key
        ) {
          return item;
        }
        changed = true;
        return {
          ...item,
          status: incoming.status,
          updatedAt: incoming.updatedAt,
          title: incoming.title,
          coverImageS3Key: incoming.coverImageS3Key,
        };
      });
      return changed ? next : prev;
    });
  }, [setWaves]);

  const patchWaveCover = useCallback(
    (waveId: string, keys: WaveCoverKeys) => {
      const apply = (wave: WaveRecord): WaveRecord => ({
        ...wave,
        ...(keys.coverImageS3Key?.trim()
          ? { coverImageS3Key: keys.coverImageS3Key.trim() }
          : {}),
        ...(keys.coverImageHeaderS3Key?.trim()
          ? { coverImageHeaderS3Key: keys.coverImageHeaderS3Key.trim() }
          : {}),
      });
      setActiveWave((prev) => (prev?.waveId === waveId ? apply(prev) : prev));
      setWaves((prev) => prev.map((w) => (w.waveId === waveId ? apply(w) : w)));
    },
    [setWaves],
  );

  const handleWaveStatusEvent = useCallback(
    (incoming: WaveRecord) => {
      setActiveWave((prev) => mergeWaveStatus(prev, incoming) ?? prev);
      patchWaveInList(incoming);
    },
    [patchWaveInList],
  );

  // Ritorna lo `spaceId` per la nuova wave. La stringa vuota "" è un valore
  // valido (wave orfana in Open Seas); solo `null` indica un errore/abort.
  const ensureSpace = async (): Promise<string | null> => {
    // Creazione dentro Open Seas → wave orfana (spaceId vuoto), instradata dal
    // projector al pointer open-sea:<ownerSub>. Nessuno spazio reale creato.
    if (isOpenSeasView) return "";
    if (selectedSpaceId && !isOpenSeaWaveSpaceId(selectedSpaceId)) {
      return selectedSpaceId;
    }
    const firstRegular = spaces.find((s) => !isReservedWaveSpace(s));
    if (firstRegular) {
      setSelectedSpaceId(firstRegular.spaceId);
      return firstRegular.spaceId;
    }
    const cfg = createWaveApiConfig();
    const space = await WaveCloudAPI.createOceanSpace(cfg, "Il mio spazio");
    setSpaces((prev) => [...prev, space]);
    setSelectedSpaceId(space.spaceId);
    return space.spaceId;
  };

  const createWave = async (template: WaveTemplate) => {
    if (!isLoggedIn) {
      startLogin();
      return;
    }
    try {
      const spaceId = await ensureSpace();
      if (spaceId === null) return;
      const cfg = createWaveApiConfig();
      const useYjs = isWaveYjsEnabled();
      const wave = await WaveCloudAPI.createWave(
        cfg,
        spaceId,
        template.title,
        template.templateId,
        template.initialStatus,
        useYjs ? WAVE_FORMAT_YJS_V1 : undefined,
      );
      if (useYjs) {
        const authorSub = currentUserSub();
        const authorName =
          currentUserEmail().trim() || authorSub;
        const seeded = stampTemplateBlocksForCreation(
          template.blocks,
          authorSub,
          authorName,
        );
        const doc = seedYDocFromBlocks(seeded);
        const snap = encodeFullYDocSnapshotB64(doc);
        await WaveCloudAPI.publishSnapshot(
          cfg,
          wave.waveId,
          snap,
          0,
          WAVE_FORMAT_YJS_V1,
        );
      } else {
        const authorSub = currentUserSub();
        const authorName =
          currentUserEmail().trim() || authorSub;
        const seeded = stampTemplateBlocksForCreation(
          template.blocks,
          authorSub,
          authorName,
        );
        const snap = encodeSnapshotB64({ blocks: seeded, version: 1 });
        if (snap) {
          await WaveCloudAPI.publishSnapshot(cfg, wave.waveId, snap, 0);
        }
      }
      if (spaceId === "") {
        // Wave orfana (Open Seas): compare via pool participating owned.
        void refreshParticipating();
      } else if (spaceId === selectedSpaceId) {
        setWaves((prev) => [wave, ...prev]);
      }
      recordOpen(wave.waveId);
      void refreshOwnedWaves();
      if (needsAnyWaveCover(wave)) {
        void requestWaveCover(wave, false, (keys) => {
          patchWaveCover(wave.waveId, keys);
        });
      }
      navigateToWave(wave);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore creazione wave");
    }
  };

  const sharedByMeIds = useMemo(
    () => new Set(sharedByMeWaves.map((w) => w.waveId)),
    [sharedByMeWaves],
  );
  const me = currentUserSub();
  const sharedOnly = useMemo(
    () =>
      sharedWaves.filter(
        (w) => w.ownerSub !== me && me !== "anonymous" && !sharedByMeIds.has(w.waveId),
      ),
    [sharedWaves, me, sharedByMeIds],
  );

  const activeSpaceWaves = useMemo(() => activeWaves(waves), [waves]);
  const activeSharedByMe = useMemo(() => activeWaves(sharedByMeWaves), [sharedByMeWaves]);
  const activeSharedOnly = useMemo(() => activeWaves(sharedOnly), [sharedOnly]);
  const activeShared = useMemo(() => {
    const seen = new Set<string>();
    return [...activeSharedByMe, ...activeSharedOnly].filter((wave) => {
      if (seen.has(wave.waveId)) return false;
      seen.add(wave.waveId);
      return true;
    });
  }, [activeSharedByMe, activeSharedOnly]);
  const favoriteWaves = useMemo(() => {
    const seen = new Set<string>();
    const all = [...activeSpaceWaves, ...activeSharedByMe, ...activeSharedOnly];
    return all.filter((wave) => {
      if (!favoriteWaveIds.has(wave.waveId) || seen.has(wave.waveId)) return false;
      seen.add(wave.waveId);
      return true;
    });
  }, [activeSpaceWaves, activeSharedByMe, activeSharedOnly, favoriteWaveIds]);

  const activeOwnedWaves = useMemo(() => activeWaves(ownedWaves), [ownedWaves]);
  const activeParticipating = useMemo(() => activeWaves(sharedWaves), [sharedWaves]);

  const inboxWavePool = useMemo(() => {
    const seen = new Set<string>();
    return [...activeOwnedWaves, ...activeSharedByMe, ...activeParticipating].filter((wave) => {
      if (seen.has(wave.waveId)) return false;
      seen.add(wave.waveId);
      return true;
    });
  }, [activeOwnedWaves, activeSharedByMe, activeParticipating]);

  // Open Seas (contenuti senza spazio): wave orfane filtrate localmente dal
  // pool owned+participating (stesso inboxWavePool di Fix A). Nessun resolver.
  const openSeasWaves = useMemo(
    () => inboxWavePool.filter((w) => !w.spaceId),
    [inboxWavePool],
  );

  const inboxSections = useMemo(
    () =>
      filterInboxSectionsBySearch(
        buildWaveInboxSections({
          pool: inboxWavePool,
          ownerSub: me,
          lastOpenedEpochMs,
        }),
        searchText,
      ),
    [inboxWavePool, me, lastOpenedEpochMs, searchText],
  );

  const inboxSummaryCount = useMemo(
    () => inboxSections.reduce((sum, section) => sum + section.waves.length, 0),
    [inboxSections],
  );

  const setWaveArchived = useCallback(
    async (waveId: string, archived: boolean) => {
      const status = archived ? WaveStatus.Archived : WaveStatus.Draft;
      try {
        const updated = await WaveCloudAPI.updateStatus(
          createWaveApiConfig(),
          waveId,
          status,
        );
        handleWaveStatusEvent(updated);
        await refreshWaves();
        await refreshSharedByMe();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Archivio non riuscito");
      }
    },
    [handleWaveStatusEvent, refreshWaves, refreshSharedByMe],
  );

  const openWaveRenameDialog = useCallback((wave: WaveRecord) => {
    setWaveRenameDialog({ waveId: wave.waveId, title: wave.title });
    setWaveRenameTitle(wave.title);
  }, []);

  const submitWaveRename = useCallback(async () => {
    if (!waveRenameDialog) return;
    const title = waveRenameTitle.trim();
    if (!title) return;
    try {
      const updated = await WaveCloudAPI.updateWave(
        createWaveApiConfig(),
        waveRenameDialog.waveId,
        title,
      );
      handleWaveStatusEvent(updated);
      setWaveRenameDialog(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rinomina non riuscita");
    }
  }, [waveRenameDialog, waveRenameTitle, handleWaveStatusEvent]);

  const mobileTitle =
    activeWave?.title ??
    (browseScope === "inbox"
      ? "Inbox"
      : browseScope === "favorites"
        ? "Favoriti"
        : browseScope === "shared"
          ? "Condivisi"
          : browseScope === "board"
            ? "Board"
          : browseScope === "space"
            ? spaceTitleById(spaces, selectedSpaceId)
            : "Wave");

  const filteredFavoriteWaves = useMemo(
    () => filterWavesBySearch(favoriteWaves, searchText),
    [favoriteWaves, searchText],
  );
  const filteredShared = useMemo(
    () => filterWavesBySearch(activeShared, searchText),
    [activeShared, searchText],
  );
  const filteredSpaceWaves = useMemo(
    () => filterWavesBySearch(activeSpaceWaves, searchText),
    [activeSpaceWaves, searchText],
  );
  const filteredOpenSeasWaves = useMemo(
    () => filterWavesBySearch(activeWaves(openSeasWaves), searchText),
    [openSeasWaves, searchText],
  );
  const filteredSpaces = useMemo(
    () => filterSpacesBySearch(spaces, searchText),
    [spaces, searchText],
  );
  // Spazi riservati (Open Seas) pinnati in cima; gli altri ordinati per titolo.
  const orderedSpaces = useMemo(() => {
    const reserved = filteredSpaces.filter((s) => isReservedWaveSpace(s));
    const regular = filteredSpaces.filter((s) => !isReservedWaveSpace(s));
    return [...reserved, ...regular];
  }, [filteredSpaces]);
  const spaceViewWaves = isOpenSeasView ? filteredOpenSeasWaves : filteredSpaceWaves;

  const boardSelected = browseScope === "board" && !targetWaveId;
  const isWaveDetailOpen = Boolean(activeWave && targetWaveId === activeWave.waveId);
  const isBrowseListSurface =
    !isWaveDetailOpen &&
    location.pathname !== "/account" &&
    location.pathname !== "/activity" &&
    (browseScope !== "none" || boardSelected);
  const inboxSelected = browseScope === "inbox" && !targetWaveId;
  const favoritesSelected = browseScope === "favorites" && !targetWaveId;
  const sharedSelected = browseScope === "shared" && !targetWaveId;
  const spaceBrowseSelected =
    browseScope === "space" && !targetWaveId && Boolean(selectedSpaceId);

  if (!isLoggedIn) {
    return <ShellLoginGate onLogin={() => startLogin()} />;
  }

  return (
    <div
      className={`shell${sidebarOpen ? " shell-sidebar-open" : ""}${
        sidebarCollapsed ? " shell-sidebar-collapsed" : ""
      }`}
    >
      <header className="shell-mobile-bar">
        <button
          type="button"
          className="icon-btn"
          aria-label={sidebarOpen ? "Chiudi menu" : "Apri menu"}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen((o) => !o)}
        >
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <span className="shell-mobile-title">{mobileTitle}</span>
      </header>

      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Chiudi menu"
          onClick={closeSidebar}
        />
      )}

      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="sidebar-scroll">
        <div className="sidebar-header">
          <WaveBrandHeader />
          <WaveShellToggleButton
            expanded
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(true)}
          />
        </div>
        <WaveSearchField value={searchText} onChange={setSearchText} />
        <section>
          <button
            type="button"
            className={inboxSelected ? "active sidebar-nav-btn" : "sidebar-nav-btn"}
            onClick={() => {
              setActiveWave(null);
              navigate("/inbox");
              closeSidebar();
            }}
          >
            <span className="sidebar-nav-btn-content">
              <WaveSidebarNavIcon name="inbox" />
              <span className="wave-sidebar-nav-label">Inbox</span>
            </span>
            {inboxSummaryCount > 0 && (
              <span className="sidebar-badge">{inboxSummaryCount}</span>
            )}
          </button>
          <button
            type="button"
            className={favoritesSelected ? "active sidebar-nav-btn" : "sidebar-nav-btn"}
            onClick={() => {
              setActiveWave(null);
              navigate("/favorites");
              closeSidebar();
            }}
          >
            <span className="sidebar-nav-btn-content">
              <WaveSidebarNavIcon name="star" />
              <span className="wave-sidebar-nav-label">Favoriti</span>
            </span>
            {favoriteWaves.length > 0 && (
              <span className="sidebar-badge">{favoriteWaves.length}</span>
            )}
          </button>
          <button
            type="button"
            className={sharedSelected ? "active sidebar-nav-btn" : "sidebar-nav-btn"}
            onClick={() => {
              setActiveWave(null);
              navigate("/shared");
              closeSidebar();
            }}
          >
            <span className="sidebar-nav-btn-content">
              <WaveSidebarNavIcon name="shared" />
              <span className="wave-sidebar-nav-label">Condivisi</span>
            </span>
            {activeShared.length > 0 && (
              <span className="sidebar-badge">{activeShared.length}</span>
            )}
          </button>
          <button
            type="button"
            className={boardSelected ? "active sidebar-nav-btn" : "sidebar-nav-btn"}
            onClick={() => {
              setActiveWave(null);
              navigate("/board");
              closeSidebar();
            }}
          >
            <span className="sidebar-nav-btn-content">
              <WaveSidebarNavIcon name="board" />
              <span className="wave-sidebar-nav-label">Board</span>
            </span>
          </button>
        </section>
        <section>
          <h3>Spazi</h3>
          {orderedSpaces.map((s) => {
            const reserved = isReservedWaveSpace(s);
            const shared = isSharedWaveSpace(s);
            const canManageMembers = !reserved && s.myRole === "OWNER";
            return (
              <div key={s.spaceId} className="space-row">
                <button
                  type="button"
                  className={`space-row-title${reserved ? " space-row-title--reserved" : ""}${
                    selectedSpaceId === s.spaceId && spaceBrowseSelected ? " active" : ""
                  }`}
                  onClick={() => {
                    setSelectedSpaceId(s.spaceId);
                    setActiveWave(null);
                    navigate(`/s/${s.spaceId}`);
                    closeSidebar();
                  }}
                >
                  {reserved && (
                    <span className="space-row-glyph" aria-hidden>
                      {s.icon ?? "🌊"}
                    </span>
                  )}
                  <span className="space-row-label">{waveSpaceDisplayTitle(s)}</span>
                  {shared && <span className="space-badge space-badge--shared">Condiviso</span>}
                </button>
                {!reserved && (
                  <details className="space-menu">
                    <summary
                      className="wave-menu-toast-btn"
                      aria-label={`Opzioni ${waveSpaceDisplayTitle(s)}`}
                    >
                      <span aria-hidden>⋯</span>
                    </summary>
                    <div className="space-menu-panel">
                      {canManageMembers && (
                        <button type="button" onClick={() => setMembersSpace(s)}>
                          Membri
                        </button>
                      )}
                      <button type="button" onClick={() => void openSpaceDialog(s, "rename")}>
                        Rinomina
                      </button>
                      <button type="button" onClick={() => void openSpaceDialog(s, "delete")}>
                        Elimina
                      </button>
                    </div>
                  </details>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              void (async () => {
                const cfg = createWaveApiConfig();
                const s = await WaveCloudAPI.createOceanSpace(cfg, "Spazio team");
                setSpaces((p) => [...p, s]);
                setSelectedSpaceId(s.spaceId);
                navigate(`/s/${s.spaceId}`);
                closeSidebar();
              })();
            }}
          >
            + Nuovo spazio
          </button>
        </section>
        </div>
        <WaveSidebarBottomCards presenceError={presenceError} />
      </aside>

      <main
        className={`main${isWaveDetailOpen ? " main--wave-workspace" : ""}${
          isBrowseListSurface ? " main--wave-browse" : ""
        }`}
      >
        {sidebarCollapsed && !activeWave && (
          <div className="main-chrome">
            <WaveShellToggleButton
              expanded={false}
              className="sidebar-reopen-btn"
              onClick={() => setSidebarCollapsed(false)}
            />
          </div>
        )}
        <div className="main-body">
        {error && (
          <div className="error-banner">
            {error}
            <button type="button" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}
        {location.pathname === "/account" ? (
          <WaveAccountPage />
        ) : location.pathname === "/activity" ? (
          <WaveActivityPage />
        ) : activeWave && targetWaveId === activeWave.waveId ? (
          <WaveWorkspace
            key={activeWave.waveId}
            wave={activeWave}
            onBack={closeWaveDetail}
            backLabel={backListLabel}
            sidebarCollapsed={sidebarCollapsed}
            onReopenSidebar={() => setSidebarCollapsed(false)}
            onWaveReplace={setActiveWave}
            onWaveStatusEvent={handleWaveStatusEvent}
            onInviteSuccess={() => void refreshSharedByMe()}
            onPresenceError={setPresenceError}
            onArchiveToggle={() =>
              void setWaveArchived(
                activeWave.waveId,
                activeWave.status !== WaveStatus.Archived,
              )
            }
            onRenameTitle={() => openWaveRenameDialog(activeWave)}
            isFavorite={isFavoriteWave(activeWave.waveId)}
            onToggleFavorite={() => toggleFavorite(activeWave.waveId)}
          />
        ) : browseScope === "board" ? (
          <div className="wave-board-page">
            <header className="wave-board-header">
              <h1>Board</h1>
              <span className="muted">
                {spaceTitleById(spaces, selectedSpaceId, "Spazio corrente")}
              </span>
            </header>
            <WaveBoardView
              waves={filteredSpaceWaves}
              onSelect={navigateToWave}
              onMove={(wave, status) => {
                void (async () => {
                  try {
                    const updated = await WaveCloudAPI.updateStatus(
                      createWaveApiConfig(),
                      wave.waveId,
                      status,
                    );
                    handleWaveStatusEvent(updated);
                    await refreshWaves();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Spostamento non riuscito");
                  }
                })();
              }}
            />
          </div>
        ) : browseScope === "inbox" ? (
          <WaveThreadGallery
            title="Inbox"
            sections={inboxSections}
            onSelect={navigateToWave}
            onCreate={() => void createWave(WaveTemplateCatalog.blank)}
            onCreateFromTemplate={(template) => void createWave(template)}
            onCoverUpdated={patchWaveCover}
            isFavorite={isFavoriteWave}
            onToggleFavorite={toggleFavorite}
          />
        ) : browseScope === "favorites" ? (
          <WaveThreadGallery
            title="Favoriti"
            waves={filteredFavoriteWaves}
            onSelect={navigateToWave}
            onCoverUpdated={patchWaveCover}
            isFavorite={isFavoriteWave}
            onToggleFavorite={toggleFavorite}
          />
        ) : browseScope === "shared" ? (
          <WaveThreadGallery
            title="Condivisi"
            waves={filteredShared}
            onSelect={navigateToWave}
            onCoverUpdated={patchWaveCover}
            isFavorite={isFavoriteWave}
            onToggleFavorite={toggleFavorite}
          />
        ) : browseScope === "space" ? (
          <WaveThreadGallery
            title={spaceTitleById(spaces, selectedSpaceId)}
            waves={spaceViewWaves}
            onSelect={navigateToWave}
            onCreate={() => void createWave(WaveTemplateCatalog.blank)}
            onCreateFromTemplate={(template) => void createWave(template)}
            onCoverUpdated={patchWaveCover}
            isFavorite={isFavoriteWave}
            onToggleFavorite={toggleFavorite}
          />
        ) : targetWaveId ? (
          <div className="empty-state">{error ?? "Caricamento wave…"}</div>
        ) : (
          <div className="empty-state">Seleziona Inbox, Favoriti, Condivisi o uno spazio</div>
        )}
        </div>
      </main>

      {waveRenameDialog && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setWaveRenameDialog(null)}
        >
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Rinomina wave</h3>
            <input
              value={waveRenameTitle}
              onChange={(e) => setWaveRenameTitle(e.target.value)}
              placeholder="Titolo wave"
              onKeyDown={(e) => {
                if (e.key === "Enter") void submitWaveRename();
              }}
            />
            <div className="modal-actions">
              <button type="button" onClick={() => setWaveRenameDialog(null)}>
                Annulla
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={!waveRenameTitle.trim()}
                onClick={() => void submitWaveRename()}
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {spaceDialog && (
        <div className="modal-backdrop" role="presentation" onClick={() => setSpaceDialog(null)}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{spaceDialog.mode === "rename" ? "Rinomina spazio" : "Elimina spazio"}</h3>
            {spaceDialog.mode === "rename" ? (
              <input
                value={spaceDialogTitle}
                onChange={(e) => setSpaceDialogTitle(e.target.value)}
                placeholder="Nome spazio"
              />
            ) : (
              <p>
                Eliminare «{spaceDialog.title}»?
                {spaceDialog.waveCount > 0 && (
                  <>
                    {" "}
                    Lo spazio contiene {spaceDialog.waveCount} wave — eliminale prima.
                  </>
                )}
              </p>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setSpaceDialog(null)}>
                Annulla
              </button>
              <button
                type="button"
                className={spaceDialog.mode === "delete" ? "danger-btn" : "primary-btn"}
                disabled={spaceDialog.mode === "delete" && spaceDialog.waveCount > 0}
                onClick={() => void submitSpaceDialog()}
              >
                {spaceDialog.mode === "rename" ? "Salva" : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}

      {membersSpace && (
        <WaveSpaceMembersPanel
          space={membersSpace}
          onClose={() => setMembersSpace(null)}
          onError={setError}
          onMembersChanged={() => void refreshSpaces()}
        />
      )}
    </div>
  );
}

function ShellLoginGate({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="shell-login">
      <h1>Oceany Wave</h1>
      <p>Collaborazione real-time — accedi per continuare.</p>
      <button type="button" className="primary-btn" onClick={onLogin}>
        Accedi con Vida
      </button>
    </div>
  );
}
